import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useMemo, useRef } from 'react';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { devWarning, utilStyles, getMs } from '@paypalcorp/pp-react-utils';
import { TransitionGroup, Transition } from 'react-transition-group';

var _templateObject, _templateObject2;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysMotionDurationArrive = tokens.sysMotionDurationArrive,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceLg = tokens.sysSpaceLg;

  var _utilStyles = utilStyles(tokens),
      variables = _utilStyles.variables;

  var ppuiEaseInOut = variables.ppuiEaseInOut;
  var container = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    position: relative;\n    height: 100%;\n    white-space: nowrap;\n    padding: 0 ", ";\n    overflow: hidden;\n  "])), sysSpaceMd);
  var panel = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    display: inline-block;\n    width: 100%;\n    padding: ", " ", "\n    height: 100%;\n    white-space: normal;\n  "])), sysSpaceLg, sysSpaceMd);
  return {
    container: container,
    panel: panel,
    defaultEasing: ppuiEaseInOut,
    defaultDurationMs: getMs(sysMotionDurationArrive)
  };
}

var styles$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  'default': styles
});

var _excluded = ["as", "className", "children"];
var Panel = function (_ref) {
  var _ref$as = _ref.as,
      Tag = _ref$as === void 0 ? 'section' : _ref$as,
      className = _ref.className,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();
  var PanelStyles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  return jsx(Tag, _extends({
    css: PanelStyles.panel,
    className: className
  }, rest, {
    "data-ppui": "true"
  }), children);
};

/*
 NOTE, the default values of duration and easing are
 failsafes. The code below should never allow these
 values to be used    --BCC
 */

/* istanbul ignore next */

function doAnimation(offset, elem) {
  var duration = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1000;
  var easing = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'linear';
  var player = elem.animate([{
    translate: offset
  }, {
    translate: 0
  }], {
    duration: duration,
    easing: easing
  });
  player.addEventListener('finish', function () {
    elem.style.translate = '0';
  });
}
/* istanbul ignore next */


var getSign = function (n) {
  return n / Math.abs(n);
};
/* istanbul ignore next */


var getDirection = function (oldId, newId) {
  return oldId < 0 ? 0 : getSign(newId - oldId);
};
/* istanbul ignore next */


var getComputedDirection = function (elem) {
  var cStyle = getComputedStyle(elem);
  return cStyle.direction === 'rtl' ? -1 : 1;
};

var PanelSlider = function (_ref) {
  var child = _ref.children,
      panelId = _ref.panelId,
      durationProp = _ref.duration,
      easingProp = _ref.easing,
      onComplete = _ref.onComplete;
  var tokens = useTheme();
  var PanelStyles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var duration = durationProp !== null && durationProp !== void 0 ? durationProp : PanelStyles.defaultDurationMs;
  var easing = easingProp !== null && easingProp !== void 0 ? easingProp : PanelStyles.defaultEasing;
  var activeScreenId = useRef(-1);
  var direction = getDirection(activeScreenId.current, panelId);
  activeScreenId.current = panelId;
  /* istanbul ignore next */
  // @ts-ignore

  return jsx("div", {
    css: PanelStyles.container,
    "data-ppui-info": "panel-slider_1.2.11"
  }, jsx(TransitionGroup, {
    "data-ppui": "true"
  }, jsx(Transition, {
    key: panelId,
    timeout: duration,
    onEnter: function onEnter(elem) {
      elem.style.translate = "".concat(direction * getComputedDirection(elem) * 100, "%");
      elem.style.left = '0';
    },
    onEntering: function onEntering(elem) {
      var offset = "".concat(direction * getComputedDirection(elem) * 100, "%");
      doAnimation(offset, elem, duration, easing);
    },
    onExit: function onExit(elem) {
      var _activeScreenId$curre;

      var dir = getDirection((_activeScreenId$curre = activeScreenId === null || activeScreenId === void 0 ? void 0 : activeScreenId.current) !== null && _activeScreenId$curre !== void 0 ? _activeScreenId$curre : -1, panelId) * getComputedDirection(elem);
      elem.style.left = "".concat(dir * 100, "%");
      elem.style.translate = "".concat(-dir * 100, "%");
      elem.style.position = 'absolute';
    },
    onExiting: function onExiting(elem) {
      var _activeScreenId$curre2;

      var dir = getDirection((_activeScreenId$curre2 = activeScreenId === null || activeScreenId === void 0 ? void 0 : activeScreenId.current) !== null && _activeScreenId$curre2 !== void 0 ? _activeScreenId$curre2 : -1, panelId) * getComputedDirection(elem);
      var offset = "".concat(-dir * 100, "%");
      doAnimation(offset, elem, duration, easing);
    },
    onEntered: function announceArrival(elem) {
      if (typeof onComplete === 'function') {
        onComplete(elem);
      }
    },
    "data-ppui": "true"
  }, child)));
};

export { Panel, PanelSlider, styles$1 as panelStyles };
